import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { NavArrowDown } from 'iconoir-react';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useTheme } from 'components/core/theme-provider';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';
import { SIDEBAR_INNER_SCROLL_CLASS } from 'constant/chat-layout';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useUpdateProject } from '../hooks/use-create-project';
import { useProjectOptions } from '../hooks/use-projects';

type ProjectSelectorProps = {
  /** Disable while a reply is streaming (same idea as model / voice controls). */
  disabled?: boolean;
};

/** "Choose Project" dropdown; picking a project attaches this conversation and stays on chat. */
export const ProjectSelector = ({ disabled = false }: ProjectSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { search: searchParams } = useLocation();
  const { conversationId: conversationIdFromParams, projectId } = useParams();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const { mutateAsync: updateProject, isPending } = useUpdateProject();
  const projects = useProjectOptions();
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations,
  );
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);

  const selectedProject = projects.find((project) => project.id === projectId);
  const searchQuery = search.trim().toLowerCase();
  const filteredProjects = searchQuery
    ? projects.filter((project) => project.name?.toLowerCase().includes(searchQuery))
    : projects;
  const textColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const isDisabled = disabled || isPending;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (isDisabled) setOpen(false);
  }, [isDisabled]);

  const handleSelect = async (id: string) => {
    setOpen(false);
    if (id === projectId) return;

    const conversationId = conversationIdFromParams || activeConversationId;
    if (!conversationId) return;

    try {
      await updateProject({
        payload: { conversationId, projectId: id },
        skipInvalidate: true,
      });

      const projectRefreshes = [fetchAndUpdateProjectAllConversations(configurationSettings, id)];
      if (projectId) {
        projectRefreshes.push(
          fetchAndUpdateProjectAllConversations(configurationSettings, projectId),
        );
      }
      await Promise.all(projectRefreshes);
      await fetchSidebarConversations(configurationSettings, {
        page: 0,
        pageSize: 50,
        isGroupConversation: false,
        includeAgentConversations: true,
      });

      navigate(`/chat/${conversationId}/${id}${searchParams}`, { replace: true });
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    }
  };

  return (
    <Popover
      open={isDisabled ? false : open}
      onOpenChange={(next) => {
        if (isDisabled) return;
        setOpen(next);
      }}
      modal={false}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          data-buddy-tour-target="choose-project"
          aria-expanded={open}
          disabled={isDisabled}
          className={cn(
            'max-w-36 sm:max-w-40 md:max-w-56 min-w-0 justify-between gap-2 h-auto px-0 py-1 transition-colors duration-200 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none',
            textColor,
            isDisabled && 'opacity-50 cursor-not-allowed hover:opacity-50',
          )}
        >
          {/* Button forces descendant svgs to size-4. The folder fills its box edge to edge,
              so 20px reads the same size as the 24px agent logo beside it. */}
          <ProjectFolderIcon
            color={selectedProject?.color ?? 'currentColor'}
            className="!size-5"
            strokeWidth={1}
          />
          <span
            className={cn(
              'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-body',
              textColor,
            )}
            title={selectedProject?.name}
          >
            {selectedProject?.name ?? t('CHOOSE_PROJECT', { defaultValue: 'Choose Project' })}
          </span>
          <NavArrowDown
            width={16}
            height={16}
            strokeWidth={1.75}
            className={cn('shrink-0 transition-transform duration-200', open && 'rotate-180')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        onOpenAutoFocus={(event) => {
          if (globalThis.matchMedia?.('(max-width: 639px)')?.matches) {
            event.preventDefault();
          }
        }}
        className="flex max-h-[min(18rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden"
      >
        <Command shouldFilter={false} className="max-h-full overflow-hidden">
          <CommandInput
            placeholder={t('SEARCH_PROJECTS', { defaultValue: 'Search projects...' })}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className={cn('min-h-0 max-h-72 pt-1', SIDEBAR_INNER_SCROLL_CLASS)}>
            <CommandEmpty>
              {projects.length === 0
                ? t('NO_PROJECTS_YET', { defaultValue: 'No projects yet' })
                : t('NO_RESULTS_FOUND')}
            </CommandEmpty>
            <CommandGroup>
              {filteredProjects.map((project) => (
                <CommandItem
                  key={project.id}
                  value={project.id}
                  onSelect={() => handleSelect(project.id)}
                  className="gap-2"
                >
                  <ProjectFolderIcon color={project.color} className="!size-5" strokeWidth={1} />
                  <span className="flex-1 truncate" title={project.name}>
                    {project.name}
                  </span>
                  {project.id === projectId && <Check className="!size-4 shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
